import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import AutoPhotoSlider from '../ui/AutoPhotoSlider';

const slides = Array.from({ length: 10 }).map((_, idx) => ({
  image: `/images/slides/slide${idx + 1}.jpg`,
  title: `SJ Swimming slide ${idx + 1}`,
}));

const HeroSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '55vh', sm: '65vh', md: '80vh' },
        minHeight: '400px',
        maxHeight: '900px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      {/* Slider Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: -2,
        }}
      >
        <AutoPhotoSlider slides={slides} />
      </Box>

      {/* Dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          zIndex: -1,
        }}
      />

      {/* Hero Content */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.93, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
        sx={{
          zIndex: 1,
          width: { xs: '90%', sm: 'auto' },
          maxWidth: { xs: '90%', sm: '80%', md: 780 },
          px: { xs: 3, sm: 6 },
          py: { xs: 4, sm: 6 },
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(10, 20, 60, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}
      >
        <Typography
          component={motion.h2}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          variant="h2"
          sx={{
            fontWeight: 700,
            mb: { xs: 1.5, sm: 2 },
            color: theme.palette.text.primary,
            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.6rem' },
          }}
        >
          Building Confident Swimmers
        </Typography>
        <Typography
          component={motion.p}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.38, ease: 'easeOut' }}
          variant="subtitle1"
          sx={{
            mb: { xs: 3, sm: 4 },
            color: 'rgba(255,255,255,0.8)',
            fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
            fontWeight: 400,
          }}
        >
          Expert swim lessons for all ages - Knox &amp; Pymble
        </Typography>

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.54, ease: 'easeOut' }}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <Button
            variant="contained"
            size="large"
            href="/programs"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: '#ffffff',
              fontWeight: 700,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 0 20px ${theme.palette.primary.main}88`,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            View Programs
          </Button>
          <Button
            variant="outlined"
            size="large"
            href="/bookings"
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255,255,255,0.6)',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Enrol Now
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
